// =============================================================================
// Priority Spel V3 — Mock backend
// -----------------------------------------------------------------------------
// Simulates the SERVER-AUTHORITATIVE contract so the frontend can be built and
// demoed end-to-end without the real Laravel API. State is persisted to
// localStorage so refresh/crash-recovery (§6) and resets (§16) are testable.
//
// Every method returns a Promise resolving to a plain object (mirrors HTTP JSON).
// The real backend must implement the SAME method names & payload/return shapes;
// see src/services/apiClient.js for the swap point.
// =============================================================================

import { TIERS, DEFAULT_TIER, CYCLE, WHEEL, MINIGAMES, resolveStars } from '@/config/gameConfig'
import { PRIZES } from './prizes'

const STORAGE_KEY = 'pg_v3_mock_state'
const LATENCY = 180 // ms, to mimic network

const delay = (ms = LATENCY) => new Promise((r) => setTimeout(r, ms))

// SMS verification limits, mirroring the server (config v3.sms): see requestMsisdnCode below.
const SMS_WINDOW_MS = 15 * 60 * 1000
const SMS_MAX_REQUESTS = 3
const SMS_MAX_ATTEMPTS = 5
const sms = { requests: [], attempts: 0, hasCode: false }
const clone = (o) => JSON.parse(JSON.stringify(o))
const uid = () => Math.random().toString(16).slice(2, 12)

function defaultState() {
  const tier = DEFAULT_TIER
  return {
    player: {
      uuid: uid(),
      tier, // stub — real source (JWE/VZ API) TBD
      email: 'prefilled@email.com', // §13.3 pre-filled known address
      emailValidated: false,
      msisdn: null,
      msisdnVerified: false,
      optinCommunication: false, // §13.1
      optinCommunicationSettled: false, // settled for the current cycle
      optinReminder: null, // §13.2 (null = never asked)
      optinReminderAsked: false, // asked once per player
    },
    cycle: {
      id: 1,
      week: 1,
      weeksTotal: CYCLE.weeks,
      // How many of this cycle's weeks the player actually PLAYED in (§14). Not
      // the same as cycle.week: a player can sit a week out. Counted once per
      // week, the first time a turn is spent.
      weeksPlayed: 0,
      starBalance: 0,
      bonusStarGranted: false, // §11 (per cycle)
      prizeBets: {}, // { [prizeId]: cumulative stars } (§10/§11)
    },
    week: {
      turnsTotal: TIERS[tier].turnsPerWeek,
      turnsRemaining: TIERS[tier].turnsPerWeek,
      played: false, // a turn was spent this week (feeds cycle.weeksPlayed)
      extraSpelGranted: false, // §4.2 once/week
      extraSterrenGranted: false, // §4.3 once/week
    },
    activeGame: null, // §6 { gameId } — a reserved, unfinished turn
    lastGameId: null, // §4.1 no repeat twice in a row
    seenGlobalIntro: false, // §3
  }
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    /* ignore */
  }
  const s = defaultState()
  save(s)
  return s
}

function save(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch {
    /* ignore quota/private-mode */
  }
}

let state = load()

// Grant the per-cycle bonus star lazily on entry (§11: "staat bij terugkomst al
// klaar", not re-granted on refresh).
function ensureCycleBonus() {
  if (!state.cycle.bonusStarGranted && state.cycle.id > 1) {
    state.cycle.starBalance += CYCLE.bonusStarOnRejoin
    state.cycle.bonusStarGranted = true
    save(state)
  }
}

function snapshot() {
  return clone(state)
}

// --- Wheel outcome (server decides) — §4 -------------------------------------
function pickOutcome() {
  const pool = []
  const w = WHEEL.weights
  // minigames as one weighted bucket (equal chance among themselves, no repeat)
  pool.push({ kind: 'minigame', weight: w.minigame })
  if (!state.week.extraSpelGranted) pool.push({ kind: 'extra_spel', weight: w.extra_spel })
  if (!state.week.extraSterrenGranted) pool.push({ kind: 'extra_sterren', weight: w.extra_sterren })

  const total = pool.reduce((a, p) => a + p.weight, 0)
  let roll = Math.random() * total
  let chosen = pool[0]
  for (const p of pool) {
    if ((roll -= p.weight) <= 0) {
      chosen = p
      break
    }
  }

  if (chosen.kind === 'minigame') {
    const candidates = MINIGAMES.filter((g) => g.id !== state.lastGameId)
    const list = candidates.length ? candidates : MINIGAMES
    const game = list[Math.floor(Math.random() * list.length)]
    return { type: 'minigame', gameId: game.id }
  }
  if (chosen.kind === 'extra_spel') return { type: 'extra_spel' }
  return { type: 'extra_sterren', amount: WHEEL.extraSterren.amount }
}

// =============================================================================
// Public API (contract)
// =============================================================================
export const mockBackend = {
  async authenticate(/* { token | uuid } */) {
    await delay()
    ensureCycleBonus()
    return { uuid: state.player.uuid, state: snapshot() }
  },

  async getState() {
    await delay(60)
    ensureCycleBonus()
    return { state: snapshot() }
  },

  async markIntroSeen() {
    await delay(40)
    state.seenGlobalIntro = true
    save(state)
    return { state: snapshot() }
  },

  // Draw the wheel. A minigame reserves the active turn (§6); specials cost none.
  async spin() {
    await delay()
    if (state.activeGame) {
      // an unfinished game exists — must be resolved first
      return { error: 'active_game', state: snapshot() }
    }
    if (state.week.turnsRemaining <= 0) {
      return { error: 'no_turns', state: snapshot() }
    }
    const outcome = pickOutcome()
    if (outcome.type === 'minigame') {
      state.week.turnsRemaining -= 1 // reserve the turn
      if (!state.week.played) {
        state.week.played = true
        state.cycle.weeksPlayed += 1
      }
      state.activeGame = { gameId: outcome.gameId }
    } else if (outcome.type === 'extra_spel') {
      state.week.turnsRemaining += 1
      state.week.turnsTotal += 1
      state.week.extraSpelGranted = true
    } else if (outcome.type === 'extra_sterren') {
      state.cycle.starBalance += outcome.amount
      state.week.extraSterrenGranted = true
    }
    save(state)
    return { outcome, state: snapshot() }
  },

  // Finish a minigame with a result → server resolves stars (§7).
  async finishGame({ gameId, score = 0, attempts, completed = true }) {
    await delay()
    const game = MINIGAMES.find((g) => g.id === gameId)
    const starsEarned = game ? resolveStars(game.starRules, { score, attempts, completed }) : 0
    state.cycle.starBalance += starsEarned
    state.lastGameId = gameId // §4.1
    state.activeGame = null
    save(state)
    return { starsEarned, state: snapshot() }
  },

  // Early quit (§5.2) → 0 stars, turn already consumed, still counts as last game.
  async quitGame({ gameId }) {
    await delay(80)
    state.lastGameId = gameId
    state.activeGame = null
    save(state)
    return { starsEarned: 0, state: snapshot() }
  },

  async getShop() {
    await delay(80)
    return { prizes: clone(PRIZES), bets: clone(state.cycle.prizeBets), state: snapshot() }
  },

  // Confirm shop allocation (§10.1) — definitive; subtract from balance, add to
  // cumulative per-prize bet. `bets` = { [prizeId]: starsToPlaceNow }.
  async confirmShop({ bets = {} }) {
    await delay()
    const total = Object.values(bets).reduce((a, n) => a + (Number(n) || 0), 0)
    if (total > state.cycle.starBalance) {
      return { error: 'insufficient_stars', state: snapshot() }
    }
    for (const [prizeId, n] of Object.entries(bets)) {
      const add = Number(n) || 0
      if (add <= 0) continue
      state.cycle.prizeBets[prizeId] = (state.cycle.prizeBets[prizeId] || 0) + add
    }
    state.cycle.starBalance -= total
    save(state)
    return { placed: total, state: snapshot() }
  },

  // --- Opt-ins & email (§13) ---------------------------------------------------
  // Mirrors the server's SMS limits (security V3-05) with the same result shapes: 3 code requests
  // per 15 minutes, 5 attempts per code. Kept in `sms` (in memory), not in `state`, so the
  // snapshot shape stays identical to the server's.
  async requestMsisdnCode({ msisdn }) {
    await delay()
    const now = Date.now()
    sms.requests = sms.requests.filter((t) => now - t < SMS_WINDOW_MS)
    if (sms.requests.length >= SMS_MAX_REQUESTS) {
      const retryAfter = Math.ceil((SMS_WINDOW_MS - (now - sms.requests[0])) / 1000)
      return { ok: false, error: 'too_many_requests', retryAfter, state: snapshot() }
    }
    sms.requests.push(now)
    sms.attempts = 0
    sms.hasCode = true
    state.player.msisdn = msisdn
    state.player.msisdnVerified = false
    save(state)
    return { ok: true, state: snapshot() } // mock always "sends" code 12345
  },

  async verifyMsisdn({ code }) {
    await delay()
    if (!sms.hasCode) return { ok: false, error: 'no_code', state: snapshot() }
    if (String(code).trim() === '12345') {
      sms.hasCode = false
      state.player.msisdnVerified = true
      save(state)
      return { ok: true, state: snapshot() }
    }
    sms.attempts += 1
    if (sms.attempts >= SMS_MAX_ATTEMPTS) {
      sms.hasCode = false
      return { ok: false, error: 'too_many_attempts', state: snapshot() }
    }
    return {
      ok: false,
      error: 'invalid_code',
      attemptsLeft: SMS_MAX_ATTEMPTS - sms.attempts,
      state: snapshot(),
    }
  },

  async setCommunicationOptin({ accept }) {
    await delay(80)
    state.player.optinCommunication = !!accept
    if (accept) state.player.optinCommunicationSettled = true // §13.1 stop asking once accepted
    save(state)
    return { state: snapshot() }
  },

  async setReminderOptin({ accept }) {
    await delay(80)
    state.player.optinReminder = !!accept
    state.player.optinReminderAsked = true // §13.2 asked once per player
    save(state)
    return { state: snapshot() }
  },

  async validateEmail({ email }) {
    await delay()
    state.player.email = email
    state.player.emailValidated = true // §13.3 once per player
    save(state)
    return { state: snapshot() }
  },

  // --- Dev/test helpers (simulate §16 resets; not part of the real API) --------
  async _devWeeklyReset() {
    const t = TIERS[state.player.tier] || TIERS[DEFAULT_TIER]
    state.week = {
      turnsTotal: t.turnsPerWeek,
      turnsRemaining: t.turnsPerWeek,
      played: false,
      extraSpelGranted: false,
      extraSterrenGranted: false,
    }
    state.activeGame = null // unfinished game from prev week expires (§6)
    if (state.cycle.week < state.cycle.weeksTotal) state.cycle.week += 1
    // keep: starBalance, prizeBets, lastGameId, optins, email, bonus status
    save(state)
    return { state: snapshot() }
  },

  async _devCycleReset() {
    state.cycle = {
      id: state.cycle.id + 1,
      week: 1,
      weeksTotal: CYCLE.weeks,
      weeksPlayed: 0,
      starBalance: 0,
      bonusStarGranted: false,
      prizeBets: {},
    }
    const t = TIERS[state.player.tier] || TIERS[DEFAULT_TIER]
    state.week = {
      turnsTotal: t.turnsPerWeek,
      turnsRemaining: t.turnsPerWeek,
      played: false,
      extraSpelGranted: false,
      extraSterrenGranted: false,
    }
    state.activeGame = null
    state.lastGameId = null
    // keep person-bound: email/emailValidated, reminder opt-in (asked once/player)
    state.player.optinCommunication = false // §13.1 asked again next cycle
    state.player.optinCommunicationSettled = false
    save(state)
    return { state: snapshot() }
  },

  async _devReset() {
    state = defaultState()
    save(state)
    return { state: snapshot() }
  },
}
